<template>
  <div data-testid="bookclosure-project-list" class="cycles-page page-table-layout">
    <v-card
      data-testid="bookclosure-project-list__main-card"
      class="all-width flex-card shadow-off"
      :loading="loading"
      :disabled="loading && !initialLoading"
      loader-height="2"
    >
      <v-card-text class="page-table-layout__body">
        <div v-if="initialLoading" data-testid="bookclosure-project-list__skeleton-loader">
          <v-row class="mb-4">
            <v-col cols="6">
              <v-skeleton-loader type="heading" class="mb-2" />
              <v-skeleton-loader type="text" />
            </v-col>
            <v-col cols="6" class="d-flex justify-end">
              <v-skeleton-loader type="button" width="120" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-skeleton-loader type="table-heading, table-row-divider@8" />
            </v-col>
          </v-row>
        </div>

        <template v-else>
          <CyclesPageHeader
            :loading="loading"
            :can-add="canAddCycle"
            :show-add-button="!isTrueEmpty"
            @refresh="refreshData"
            @add-cycle="handleAddCycle"
          />

          <CyclesToolbar
            v-if="!isTrueEmpty"
            :cycles-filters="cyclesFilters"
            :filter-options="filterOptions"
            :disabled="loading"
            :upsi-enabled="upsiEnabled"
            @update:cyclesFilters="onCyclesFiltersUpdate"
          />

          <CyclesEmptyState
            v-if="isTrueEmpty"
            variant="no-cycles"
            :can-add="canAddCycle"
            :action-disabled="loading"
            @action="handleAddCycle"
          />

          <CyclesEmptyState
            v-else-if="isPeriodFilterEmpty"
            variant="period-filter"
            :period-label="periodFilterLabel"
            :hide-action="isDefaultPeriodSelection"
            :action-disabled="loading"
            @action="handleClearPeriodFilter"
          />

          <CyclesEmptyState
            v-else-if="isOtherFilterEmpty"
            variant="filters"
            :action-disabled="loading"
            @action="handleResetAllFilters"
          />

          <template v-else>
            <div class="flex-fill-table">
              <CyclesTable
                :cycles="filteredCycles"
                :paginated-cycles="paginatedCycles"
                :loading="false"
                :headers="tableHeaders"
                :items-per-page="itemsPerPage"
                :userid="userid"
                :locations-map="locationsMap"
                :actor-role-label="actorRoleLabel"
                :actor-location-ids="actorLocationIds"
                :right="right"
                :upsi-enabled="upsiEnabled"
                @row-click="handleRowClick"
                @close-cycle="handleCloseCycle"
                @reopen-cycle="handleReopenCycle"
                @edit-cycle="handleEditCycle"
                @toggle-notification="handleToggleNotification"
                @retry-cycle-creation="handleRetryCycleCreation"
                @delete-cycle="handleDeleteCycle"
                @upsi-publish="handleUpsiPublish"
                @upsi-review="handleUpsiReview"
                @upsi-view-details="handleUpsiViewDetails"
              />
            </div>
          </template>

          <CycleStatusConfirmDialog
            v-model="confirmDialog.open"
            :mode="confirmDialog.mode"
            :cycle="confirmDialog.cycle"
            :confirm-label="confirmDialog.confirmLabel"
            :loading="confirmDialogLoading"
            :upsi-warning="statusConfirmUpsiWarning"
            @confirm="confirmStatusChange"
            @cancel="closeConfirmDialog"
          />

          <CycleFormDialog
            v-model="formDialog.open"
            :mode="formDialog.mode"
            :cycle="formDialog.cycle"
            :locations="actorLocationsList"
            :tags="tagsList"
            :templates="templateOptions"
            :previous-cycles="previousCycleOptions"
            :loading="formDialogLoading"
            :errors="formDialogErrors"
            @save="handleSaveCycle"
            @cancel="closeFormDialog"
          />

          <UpsiPublishDialog
            :value="upsiDialog.type === upsiDialogTypes.PUBLISH"
            :mode="upsiDialog.mode"
            :cycle="upsiDialog.cycle || {}"
            :details="upsiDialog.details"
            :loading="upsiDialog.loading"
            :server-errors="upsiDialog.serverErrors"
            @submit="submitUpsiPublish"
            @input="onUpsiDialogInput(upsiDialogTypes.PUBLISH, $event)"
            @cancel="dismissUpsiDialog(upsiDialogTypes.PUBLISH)"
          />

          <UpsiReviewDialog
            :value="upsiDialog.type === upsiDialogTypes.REVIEW"
            :cycle="upsiDialog.cycle || {}"
            :details="upsiDialog.details"
            :loading="upsiDialog.loading"
            :server-errors="upsiDialog.serverErrors"
            @submit="submitUpsiReview"
            @input="onUpsiDialogInput(upsiDialogTypes.REVIEW, $event)"
            @cancel="dismissUpsiDialog(upsiDialogTypes.REVIEW)"
          />

          <UpsiDetailsDialog
            :value="upsiDialog.type === upsiDialogTypes.DETAILS"
            :cycle="upsiDialog.cycle || {}"
            :details="upsiDialog.details"
            :loading="upsiDialog.loading"
            :can-publish-again="upsiCanPublishAgain"
            @publish-again="openUpsiRepublishFromDetails"
            @input="onUpsiDialogInput(upsiDialogTypes.DETAILS, $event)"
            @close="dismissUpsiDialog(upsiDialogTypes.DETAILS)"
          />
        </template>
      </v-card-text>
    </v-card>

    <Pagination
      v-if="!initialLoading && filteredCycles.length"
      :key="paginationKey"
      :list="filteredCycles"
      :itemsperpage="itemsPerPage"
      data-testid="bookclosure-project-list__pagination"
      @paginated-data="onPaginatedData"
    />
  </div>
</template>

<script>
import './Cycles.scss';
import { mapGetters } from 'vuex';
import {
  CyclesPageHeader,
  CyclesToolbar,
  CyclesTable,
  CyclesEmptyState,
} from './components';
import {
  CycleStatusConfirmDialog,
  CycleFormDialog,
  UpsiPublishDialog,
  UpsiReviewDialog,
  UpsiDetailsDialog,
} from './modals';
import { Pagination } from '../../components';
import {
  createInitialCyclesFilter,
  DEFAULT_CYCLES_FILTERS,
  ITEMS_PER_PAGE,
  POLL_INTERVAL_MS,
  TABLE_HEADERS,
  POPULATE_CHECKLIST_ITEMS,
} from './utils/cyclesConstants';
import {
  applyCycleFilters,
  buildFilterOptions,
  flattenCycleFieldErrors,
  canExportCycleActivityLogs,
  formatCycleFieldErrorToast,
  formatCycleRow,
  formatPeriodFilterLabel,
  getCloseConfirmLabel,
  hasActiveFilters,
  listNeedsCreationPoll,
  mergeLiteCycleFields,
  polledCycleReachedEndState,
} from './utils/cyclesHelpers';
import {
  CYCLE_MESSAGES,
  FC_ROUTES,
  UPSI_MESSAGES,
  UPSI_STAGES,
} from '../../utils/constants';
import { openSignedExportUrl } from '../../utils/activityLogExport';
import { dropIdsNotAllowed } from '../../utils/locationUserAccess';
import { isCurrentYearAllPeriodSelection } from '../../utils/periodHelpers';
import constants from '../../../../plugins/constants';
import cyclesUpsiHandlers, {
  canPublishUpsiAgain,
  createEmptyUpsiDialog,
  upsiPatchFromReopenResponse,
} from './utils/cyclesUpsiHandlers';

export default {
  name: 'Cycles',

  components: {
    CyclesPageHeader,
    CyclesToolbar,
    CyclesTable,
    CycleStatusConfirmDialog,
    CycleFormDialog,
    CyclesEmptyState,
    Pagination,
    UpsiPublishDialog,
    UpsiReviewDialog,
    UpsiDetailsDialog,
  },

  mixins: [cyclesUpsiHandlers],

  data() {
    return {
      formattedCycles: [],
      filteredCycles: [],
      paginatedCycles: [],
      itemsPerPage: ITEMS_PER_PAGE,
      currentPage: 1,
      filterOptions: {
        locations: [],
        users: [],
        periods: [],
        availableMonthTokens: [],
      },
      locationsList: [],
      actorLocationsList: [],
      actorRoleLabel: '',
      usersList: [],
      tagsList: [],
      locationsMap: {},
      hasChartOfAccounts: true,
      confirmDialog: { open: false, mode: 'close', cycle: null, confirmLabel: '' },
      confirmDialogLoading: false,
      formDialog: { open: false, mode: 'add', cycle: null },
      formDialogLoading: false,
      formDialogErrors: {},
      templateOptions: POPULATE_CHECKLIST_ITEMS,
      initialLoading: true,
      loading: false,
      pollingInterval: null,
      pollGeneration: 0,
      attachmentExportHandled: false,
      paginationKey: 0,
      right: 'financialclose_project',
      upsiDialog: createEmptyUpsiDialog(),
    };
  },

  computed: {
    ...mapGetters('project', ['projectList', 'cyclesFilter']),

    cyclesFilters: {
      get() {
        return this.cyclesFilter || { ...DEFAULT_CYCLES_FILTERS };
      },
      set(val) {
        this.$store.commit('project/setCyclesFilter', val);
      },
    },

    userid() {
      return this.$store.state.user?.id;
    },

    canAddCycle() {
      return (
        this.isActorController
        && this.$nova?.hasRight(this.right, 'add')
      );
    },

    isActorController() {
      return String(this.actorRoleLabel || '').trim() === 'controller';
    },

    actorLocationIds() {
      return (this.actorLocationsList || []).map((loc) => String(loc._id)).filter(Boolean);
    },

    /**
     * Location filter options. Controllers see the full catalog (`locations`).
     * Everyone else sees `actorLocations` (role already applied by getformdata).
     */
    locationFilterLocations() {
      return this.isActorController ? this.locationsList : this.actorLocationsList;
    },

    filtersAreActive() {
      return hasActiveFilters(this.cyclesFilters);
    },

    /** No cycles returned from API at all. */
    isTrueEmpty() {
      return !this.initialLoading && (this.formattedCycles || []).length === 0;
    },

    hasFilteredResults() {
      return (this.filteredCycles || []).length > 0;
    },

    hasPeriodFilter() {
      return (this.cyclesFilters?.periods || []).length > 0;
    },

    isDefaultPeriodSelection() {
      return isCurrentYearAllPeriodSelection(this.cyclesFilters?.periods);
    },

    /** Cycles exist, but period filter yields none. */
    isPeriodFilterEmpty() {
      return (
        !this.isTrueEmpty &&
        !this.hasFilteredResults &&
        this.hasPeriodFilter
      );
    },

    /** Cycles exist, other filters yield none (no period selection). */
    isOtherFilterEmpty() {
      return (
        !this.isTrueEmpty &&
        !this.hasFilteredResults &&
        !this.hasPeriodFilter &&
        this.filtersAreActive
      );
    },

    periodFilterLabel() {
      return formatPeriodFilterLabel(this.cyclesFilters?.periods || []);
    },

    previousCycleOptions() {
      return (this.formattedCycles || []).map((c) => ({
        _id: c._id,
        description: c.description,
      }));
    },

    upsiEnabled() {
      return !!this.$store.getters['tasks/getFormDataV3']?.upsiEnabled;
    },

    tableHeaders() {
      if (this.upsiEnabled) return TABLE_HEADERS;
      return TABLE_HEADERS.filter((header) => header.value !== 'upsi');
    },

    upsiCanPublishAgain() {
      const cycle = this.upsiDialog && this.upsiDialog.cycle;
      const canPublish = canExportCycleActivityLogs({
        cycle,
        userId: this.userid,
        actorRoleLabel: this.actorRoleLabel,
        actorLocationIds: this.actorLocationIds,
      });
      return canPublishUpsiAgain(
        this.upsiDialog && this.upsiDialog.details,
        canPublish,
        cycle
      );
    },

    statusConfirmUpsiWarning() {
      if (!this.upsiEnabled || this.confirmDialog.mode !== 'reopen') return '';
      const row = this.confirmDialog.cycle || {};
      const listed = (this.projectList || []).find((item) => item && item._id === row._id);
      const upsiState = row.upsi_state || (listed && listed.upsi_state) || {};
      const stage = Number(upsiState.stage);
      if (stage === UPSI_STAGES.PENDING_APPROVAL) return UPSI_MESSAGES.REOPEN_WARNING_AWAITING;
      if (stage === UPSI_STAGES.PUBLISHED) return UPSI_MESSAGES.REOPEN_WARNING_PUBLISHED;
      if (stage === UPSI_STAGES.REJECTED) return UPSI_MESSAGES.REOPEN_WARNING_REJECTED;
      return '';
    },
  },

  watch: {
    cyclesFilter: {
      deep: true,
      handler() {
        this.applyFilters({ resetPage: true });
      },
    },
    '$store.state.client'() {
      this.clearPolling();
    },
  },

  created() {
    this.init();
  },

  beforeDestroy() {
    this.clearPolling();
    this.upsiDialog = createEmptyUpsiDialog();
  },

  methods: {
    async consumeAttachmentExport() {
      const exportId = this.$route.query && this.$route.query.attachmentExport;
      if (!exportId || this.attachmentExportHandled) return;
      this.attachmentExportHandled = true;
      try {
        const signed = await this.$store.dispatch('downloadCycleActivityLogExport', { exportId });
        openSignedExportUrl(signed && signed.url, signed && signed.fileName);
      } catch (err) {
        console.error('[Cycles]: attachment export download error', err);
      } finally {
        // base.vue keys router-view on $route.fullPath. router.replace remounts Cycles
        // and reruns form data plus the project list. Same pattern as TasksLandingPage.
        const query = { ...this.$route.query };
        delete query.attachmentExport;
        const { href } = this.$router.resolve({ path: this.$route.path, query });
        window.history.replaceState(window.history.state, '', href);
      }
    },

    init() {
      // Home shows the cycle table only — sidebar cycle sub-nav is for an active cycle.
      // Clear any previously expanded multi-cycle menu (avoids duplicated Dashboard/Tasklist/etc.).
      this.$store.commit('resetCycles');
      this.refreshData({ resetPage: true });
    },

    async refreshData({ resetPage = false } = {}) {
      this.loading = true;
      if (!this.formattedCycles.length) {
        this.initialLoading = true;
      }

      try {
        const [formDataV3] = await Promise.all([
          this.$store.dispatch('tasks/fetchFormDataV3'),
          this.$store.dispatch('project/fetchProjectListV3'),
        ]);

        this.applyFormData(formDataV3);
        this.formatCycles();
        this.applyFilters({ resetPage });
        this._schedulePollingIfNeeded();
        await this.consumeAttachmentExport();
      } catch (err) {
        console.error('[Cycles]: refreshData error', err);
        this.$store.commit('sbError', err?.message || 'Unknown error!');
      } finally {
        this.loading = false;
        this.initialLoading = false;
      }
    },

    applyFormData(formDataV3) {
      if (!formDataV3) return;

      // Full catalog for name maps. Location filter uses locationFilterLocations.
      this.locationsList = (formDataV3.locations || []).map((loc) => ({
        _id: loc._id,
        name: loc.name,
        country: loc.country,
        type: loc.type,
      }));

      this.locationsMap = {};
      this.locationsList.forEach((loc) => {
        this.locationsMap[loc._id] = loc;
      });

      // Actor-scoped set for CycleFormDialog location picker
      this.actorLocationsList = (formDataV3.actorLocations || []).map((loc) => ({
        _id: loc._id,
        name: loc.name,
        country: loc.country,
        type: loc.type,
      }));

      this.actorRoleLabel = formDataV3.actorRoleLabel || '';

      this.usersList = (formDataV3.users || []).map((u) => ({
        _id: u._id,
        name: u.name,
        email: u.email,
        displayname: u.name ? `${u.name}(${u.email || ''})` : u.email,
        isActive: u.isActive,
      }));

      this.tagsList = formDataV3.tags || [];
      this.hasChartOfAccounts = !!formDataV3.hasChartOfAccounts;
    },

    formatCycles() {
      const list = this.projectList || [];
      this.formattedCycles = list.map((raw, index) =>
        formatCycleRow(raw, index, this.locationsMap)
      );
      this.filterOptions = buildFilterOptions(
        this.formattedCycles,
        this.locationFilterLocations,
        this.usersList
      );
      this.pruneLocationFilterToScope();
    },

    pruneLocationFilterToScope() {
      const allowedIds = (this.locationFilterLocations || []).map((loc) => loc && loc._id);
      const selected = (this.cyclesFilters && this.cyclesFilters.locations) || [];
      const next = dropIdsNotAllowed(selected, allowedIds);
      const selectedIds = selected.map((id) => String(id));
      const unchanged = next.length === selectedIds.length
        && next.every((id, index) => id === selectedIds[index]);
      if (unchanged) return;
      this.cyclesFilters = {
        ...this.cyclesFilters,
        locations: next,
      };
    },

    applyFilters({ resetPage = true } = {}) {
      this.filteredCycles = applyCycleFilters(
        this.formattedCycles,
        this.cyclesFilters,
        this.locationsMap
      );

      const maxPage = Math.ceil(this.filteredCycles.length / this.itemsPerPage) || 1;
      if (resetPage || this.currentPage > maxPage || this.currentPage < 1) {
        this.currentPage = 1;
        this.paginationKey += 1;
      }

      // Seed current page immediately; Pagination will re-emit on mount/list change.
      const start = (this.currentPage - 1) * this.itemsPerPage;
      this.paginatedCycles = this.filteredCycles.slice(start, start + this.itemsPerPage);
    },

    onCyclesFiltersUpdate(nextFilters) {
      this.cyclesFilters = nextFilters;
    },

    handleResetAllFilters() {
      this.cyclesFilters = createInitialCyclesFilter();
    },

    handleClearPeriodFilter() {
      this.cyclesFilters = {
        ...this.cyclesFilters,
        periods: [],
      };
    },

    onPaginatedData(paginationInfo) {
      if (paginationInfo && typeof paginationInfo === 'object' && 'data' in paginationInfo) {
        this.paginatedCycles = Array.isArray(paginationInfo.data) ? paginationInfo.data : [];
        this.currentPage = paginationInfo.page || 1;
      } else if (Array.isArray(paginationInfo)) {
        this.paginatedCycles = paginationInfo;
      }
    },

    handleRowClick(cycle) {
      if (!cycle?._id) return;
      try {
        localStorage.setItem(constants.STORAGE_KEYS.USER_OBJ, JSON.stringify(cycle));
      } catch (e) {
        // ignore storage failures
      }
      this.$store.commit('addItemToCycleList', cycle);
      this.$nova.gotoLink({ path: FC_ROUTES.CHECKLIST(cycle._id) });
    },

    handleAddCycle() {
      this.formDialogErrors = {};
      this.formDialog = { open: true, mode: 'add', cycle: null };
    },

    handleEditCycle(cycle) {
      this.formDialogErrors = {};
      this.formDialog = { open: true, mode: 'edit', cycle };
    },

    handleCloseCycle(cycle) {
      this.confirmDialog = {
        open: true,
        mode: 'close',
        cycle,
        confirmLabel: getCloseConfirmLabel(cycle?.summary),
      };
    },

    handleReopenCycle(cycle) {
      this.confirmDialog = {
        open: true,
        mode: 'reopen',
        cycle,
        confirmLabel: 'Reopen cycle',
      };
    },

    closeConfirmDialog() {
      this.confirmDialog = {
        open: false,
        mode: 'close',
        cycle: null,
        confirmLabel: '',
      };
      this.confirmDialogLoading = false;
    },

    closeFormDialog() {
      this.formDialog = { open: false, mode: 'add', cycle: null };
      this.formDialogLoading = false;
      this.formDialogErrors = {};
    },

    async confirmStatusChange() {
      const cycle = this.confirmDialog.cycle;
      const id = cycle?._id;
      if (!id) return;

      this.confirmDialogLoading = true;
      try {
        if (this.confirmDialog.mode === 'close') {
          await this.$store.dispatch('project/closeProject', id);
        } else {
          const response = await this.$store.dispatch('project/openProject', id);
          const patch = upsiPatchFromReopenResponse(id, response);
          if (patch) {
            console.log('[Cycles]:', { action: 'reopen-upsi', id, stage: patch.upsi_state && patch.upsi_state.stage });
            this.$store.commit('project/patchProjectUpsi', patch);
            this.formatCycles();
            this.applyFilters({ resetPage: false });
          }
        }
        this.closeConfirmDialog();
        await this.refreshData();
      } catch (err) {
        console.error('[Cycles]: confirmStatusChange error', err);
        if (err?.response?.status === 409) {
          this.closeConfirmDialog();
          await this.refreshData();
        }
      } finally {
        this.confirmDialogLoading = false;
      }
    },

    async handleSaveCycle(payload) {
      if (!payload) return;

      this.formDialogLoading = true;
      this.formDialogErrors = {};

      try {
        if (this.formDialog.mode === 'edit') {
          await this.saveEditCycle(payload);
        } else {
          await this.saveAddCycle(payload);
        }
      } catch (err) {
        if (err?.response?.status === 400) {
          this.applyCycleFormValidationErrors({ data: err.response.data });
        }
        console.error('[Cycles]: handleSaveCycle error', err);
      } finally {
        this.formDialogLoading = false;
      }
    },

    async saveAddCycle(payload) {
      const dt = await this.$store.dispatch('project/addProject', payload);

      if (dt?.data?.status === 'success') {
        const newProjectId = dt.data.project?._id;
        const newProjectDescription =
          dt.data.project?.description || payload.description;

        if (this.$store.state.erpEnabled?.tbErpEnabled && newProjectId) {
          try {
            await this.$store.dispatch('project/syncERP', {
              payload,
              id: newProjectId,
            });
          } catch (erpErr) {
            console.error('[Cycles]: syncERP after add error', erpErr);
          }
        }

        this.$store.commit(
          'sbSuccess',
          dt.data?.message || CYCLE_MESSAGES.SUCCESS.NEW_CYCLE_CREATED
        );

        if (newProjectId) {
          this.$store.commit('addItemToCycleList', {
            _id: newProjectId,
            description: newProjectDescription,
          });
        }

        this.closeFormDialog();
        await this.refreshData();
      }
    },

    async saveEditCycle(payload) {
      const id = payload._id || this.formDialog.cycle?._id;
      if (!id) return;

      const dt = await this.$store.dispatch('project/editProject', {
        id,
        data: payload,
      });

      if (dt?.data?.status === 'success') {
        this.$store.commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.PROJECT_UPDATED);
        const updatedId = dt.data.data?.[0]?._id || id;

        if (updatedId && this.$store.state.erpEnabled?.tbErpEnabled) {
          try {
            await this.$store.dispatch('project/syncERP', {
              payload,
              id: updatedId,
            });
          } catch (erpErr) {
            console.error('[Cycles]: syncERP after edit error', erpErr);
          }
        }

        this.closeFormDialog();
        await this.refreshData();
      }
    },

    applyCycleFormValidationErrors(dt) {
      const payload = dt?.data?.data;
      const raw = Array.isArray(payload) || (payload && typeof payload === 'object')
        ? payload
        : null;
      const fieldErrors = flattenCycleFieldErrors(raw);
      this.formDialogErrors = fieldErrors;
      this.$store.commit(
        'sbError',
        formatCycleFieldErrorToast(
          fieldErrors,
          dt?.data?.message || CYCLE_MESSAGES.ERROR.UNKNOWN
        )
      );
    },

    async handleToggleNotification({ id, type }) {
      if (!id) return;
      this.loading = true;
      try {
        await this.$store.dispatch('project/toggleNotification', { id, type });
        await this.refreshData();
      } catch (err) {
        console.error('[Cycles]: toggleNotification error', err);
      } finally {
        this.loading = false;
      }
    },

    async handleRetryCycleCreation(cycle) {
      const id = cycle?._id;
      if (!id) return;
      this.loading = true;
      try {
        await this.$store.dispatch('project/retryCycleCreation', id);
        await this.refreshData();
      } catch (err) {
        console.error('[Cycles]: retryCycleCreation error', err);
      } finally {
        this.loading = false;
      }
    },

    async handleDeleteCycle(cycle) {
      const id = cycle?._id;
      if (!id) return;
      this.loading = true;
      try {
        await this.$store.dispatch('project/deleteProject', id);
        await this.refreshData();
      } catch (err) {
        console.error('[Cycles]: deleteProject error', err);
      } finally {
        this.loading = false;
      }
    },

    clearPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
      this.pollGeneration += 1;
    },

    hasInProgressCycle(list) {
      return listNeedsCreationPoll(list);
    },

    _pollStillCurrent(clientId, generation) {
      return this.pollGeneration === generation
        && String(this.$store.state.client || '') === String(clientId || '');
    },

    async _pollCycleCreation(clientId, generation) {
      if (!this._pollStillCurrent(clientId, generation)) return;
      try {
        const previous = this.projectList || [];
        const lite = await this.$store.dispatch('fetchCyclesForSwitcher', { force: true });
        if (!this._pollStillCurrent(clientId, generation)) return;
        if (polledCycleReachedEndState(previous, lite)) {
          await this.$store.dispatch('project/fetchProjectListV3', { silent: true });
          if (!this._pollStillCurrent(clientId, generation)) return;
        } else {
          // previous is only for transition detection. A full-list refresh may
          // have committed while this poll was in flight, so merge into the
          // list that is current now.
          this.$store.commit(
            'project/setProjectList',
            mergeLiteCycleFields(this.projectList || [], lite),
          );
        }
        this.formatCycles();
        this.applyFilters({ resetPage: false });
        if (!this.hasInProgressCycle(this.projectList)) {
          this.clearPolling();
        }
      } catch (err) {
        console.error('[Cycles]: polling error', err);
      }
    },

    _schedulePollingIfNeeded() {
      const needsPoll = this.hasInProgressCycle(this.projectList);
      if (needsPoll && !this.pollingInterval) {
        const clientId = String(this.$store.state.client || '');
        const generation = this.pollGeneration;
        this.pollingInterval = setInterval(() => {
          this._pollCycleCreation(clientId, generation);
        }, POLL_INTERVAL_MS);
      } else if (!needsPoll) {
        this.clearPolling();
      }
    },
  },
};
</script>
